import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WEBFEE_AMOUNT = 10;

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO?: string;
    BOWLER_COUNT?: number;
}

interface WebFeeEntry {
    CVETORNEO: number;
    CVEJUGADOR: number;
    NOMBRECOMPLETO: string;
    FICHANAL: number;
    TORNEO: string | null;
    WEBFEE: number | null;
    CVEVENDEDOR_WEBFEE: number | null;
    FECHA_PAGO_WEBFEE: string | null;
}

type Tab = 'unpaid' | 'paid' | 'pos';

const WebFees = () => {
    const navigate = useNavigate();

    // â”€â”€ Tournaments â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(true);
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);

    // â”€â”€ Entries â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [entries, setEntries] = useState<WebFeeEntry[]>([]);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [entriesError, setEntriesError] = useState('');

    // â”€â”€ UI state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [activeTab, setActiveTab] = useState<Tab>('unpaid');
    const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());

    // Auth guard + load tournament list
    useEffect(() => {
        const adminStr = localStorage.getItem('loggedInAdmin');
        if (!adminStr) { navigate('/admin'); return; }
        try {
            const admin = JSON.parse(adminStr);
            if (admin.CLAVE !== 28) { navigate('/admin/dashboard'); return; }
        } catch {
            navigate('/admin');
            return;
        }

        fetch(`${BACKEND_URL}/api/admin/tournaments-with-counts`)
            .then(r => r.json())
            .then(data => setTournaments(Array.isArray(data) ? data : []))
            .catch(() => setTournaments([]))
            .finally(() => setTournamentsLoading(false));
    }, [navigate]);

    // Load entries when tournament changes
    useEffect(() => {
        if (!selectedTournament) return;
        setEntriesLoading(true);
        setEntriesError('');
        setEntries([]);
        setExcludedKeys(new Set());

        fetch(`${BACKEND_URL}/api/admin/webfees?torneoClave=${selectedTournament.CLAVE}`)
            .then(r => {
                if (!r.ok) return r.json().then(d => Promise.reject(new Error(d.error || d.details || `HTTP ${r.status}`)));
                return r.json();
            })
            .then(data => setEntries(Array.isArray(data) ? data : []))
            .catch(e => setEntriesError(e.message))
            .finally(() => setEntriesLoading(false));
    }, [selectedTournament]);

    // â”€â”€ Derived data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const unpaid = entries.filter(e => !e.WEBFEE || e.WEBFEE !== 1);
    const paid   = entries.filter(e => e.WEBFEE === 1);
    const pos    = entries.filter(
        e => e.WEBFEE === 1 &&
             e.CVEVENDEDOR_WEBFEE != null &&
             e.FECHA_PAGO_WEBFEE != null &&
             e.FECHA_PAGO_WEBFEE !== ''
    );

    const totalPaid = paid.length * WEBFEE_AMOUNT;
    const totalOwed = unpaid.length * WEBFEE_AMOUNT;

    const entryKey = (e: WebFeeEntry) => `${e.CVEJUGADOR}-${e.CVETORNEO}`;

    const toggleExclude = (e: WebFeeEntry) => {
        const key = entryKey(e);
        setExcludedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const posForInvoice   = pos.filter(e => !excludedKeys.has(entryKey(e)));
    const posInvoiceTotal = posForInvoice.length * WEBFEE_AMOUNT;

    const formatDate = (val: string | null) => {
        if (!val) return 'â€”';
        try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
        catch { return val; }
    };

    const handlePrintInvoice = () => {
        const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const rows = posForInvoice
            .map((e, i) => `
                <tr>
                    <td>${i + 1}</td>
                    <td>${e.FICHANAL}</td>
                    <td>${e.NOMBRECOMPLETO}</td>
                    <td>${e.TORNEO || 'â€”'}</td>
                    <td>${formatDate(e.FECHA_PAGO_WEBFEE)}</td>
                    <td style="text-align:right;">$${WEBFEE_AMOUNT}.00</td>
                </tr>
            `).join('');

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>Web Fee Invoice Ragetech LLC</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #111; padding: 48px; }
  .inv-header { margin-bottom: 36px; border-bottom: 3px solid #111; padding-bottom: 20px; }
  .inv-header h1 { font-size: 26px; letter-spacing: -0.5px; }
  .inv-header .date { color: #555; margin-top: 6px; }
  .parties { display: flex; gap: 60px; margin-bottom: 32px; }
  .party h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; margin-bottom: 6px; }
  .party p { font-size: 13px; margin: 2px 0; }
  .party strong { font-size: 14px; }
  .payment-request { background: #f8f8f8; border-left: 4px solid #111; padding: 14px 18px; margin-bottom: 32px; font-size: 13px; line-height: 1.6; }
  .count-line { font-size: 12px; color: #555; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
  th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 2px solid #111; padding: 8px 6px; text-align: left; }
  td { border-bottom: 1px solid #ddd; padding: 7px 6px; }
  .total-row td { border-top: 2px solid #111; border-bottom: none; padding-top: 10px; font-weight: bold; font-size: 14px; }
  .footer { margin-top: 48px; font-size: 11px; color: #777; border-top: 1px solid #ccc; padding-top: 14px; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
<div class="inv-header">
  <h1>Web Fee Invoice</h1>
  <div class="date">Date: ${today}</div>
</div>

<div class="parties">
  <div class="party">
    <h3>Bill To (Payer)</h3>
    <p><strong>True Amateur Tournaments</strong></p>
  </div>
  <div class="party">
    <h3>Payee</h3>
    <p><strong>Ragetech LLC</strong></p>
  </div>
</div>

<div class="payment-request">
  True Amateur Tournaments is hereby requested to remit payment of <strong>$${posInvoiceTotal.toFixed(2)}</strong>
  to <strong>Ragetech LLC</strong> for web registration fees collected through the online platform.
  Payment may be made by <strong>check, Zelle, or bank transfer</strong>.
</div>

<div class="count-line">Total bowlers included: <strong>${posForInvoice.length}</strong></div>

<table>
  <thead>
    <tr>
      <th>#</th>
      <th>TAT #</th>
      <th>Bowler Name</th>
      <th>Tournament</th>
      <th>Date Paid</th>
      <th style="text-align:right;">Amount</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
    <tr class="total-row">
      <td colspan="5">Total</td>
      <td style="text-align:right;">$${posInvoiceTotal.toFixed(2)}</td>
    </tr>
  </tbody>
</table>

<div class="footer">
  <p>Generated by Register2Bowl on ${today}.</p>
  <p>Please remit payment to Ragetech LLC via check, Zelle, or bank transfer.</p>
</div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(html);
            win.document.close();
            win.focus();
            setTimeout(() => win.print(), 600);
        }
    };

    const tabs: { id: Tab; label: string; count: number; active: string; inactive: string; badge: string; badgeActive: string }[] = [
        {
            id: 'unpaid', label: 'Not Paid', count: unpaid.length,
            active:      'bg-yellow-500 text-white shadow-md shadow-yellow-200',
            inactive:    'bg-white text-yellow-700 border border-yellow-200 hover:bg-yellow-50',
            badge:       'bg-yellow-100 text-yellow-700',
            badgeActive: 'bg-yellow-400 text-white',
        },
        {
            id: 'paid', label: 'Paid', count: paid.length,
            active:      'bg-green-500 text-white shadow-md shadow-green-200',
            inactive:    'bg-white text-green-700 border border-green-200 hover:bg-green-50',
            badge:       'bg-green-100 text-green-700',
            badgeActive: 'bg-green-400 text-white',
        },
        {
            id: 'pos', label: 'Paid at POS', count: pos.length,
            active:      'bg-indigo-500 text-white shadow-md shadow-indigo-200',
            inactive:    'bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50',
            badge:       'bg-indigo-100 text-indigo-700',
            badgeActive: 'bg-indigo-400 text-white',
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/admin/dashboard')}
                                className="text-teal-100 hover:text-white transition flex items-center gap-1"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Dashboard
                            </button>
                            <div className="w-px h-6 bg-teal-500" />
                            <h1 className="text-2xl font-bold text-white">Web Fees</h1>
                            {selectedTournament && (
                                <span className="text-teal-200 text-sm font-medium"> {selectedTournament.NOMBRE}</span>
                            )}
                        </div>
                        {selectedTournament && (
                            <div className="flex items-center gap-6">
                                <div className="text-center">
                                    <div className="text-black text-xs uppercase tracking-wider mb-0.5">Total Paid</div>
                                    <div className="text-green-500 text-2xl font-bold">${totalPaid.toLocaleString()}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-black text-xs uppercase tracking-wider mb-0.5">Total Owed</div>
                                    <div className="text-yellow-500 text-2xl font-bold">${totalOwed.toLocaleString()}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-black text-xs uppercase tracking-wider mb-0.5">Entries</div>
                                    <div className="text-blue-500 text-2xl font-bold">{entries.length}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Tournament Selector */}
                <div className="bg-white rounded-2xl shadow p-5 mb-8">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Select Tournament</label>
                    {tournamentsLoading ? (
                        <div className="h-10 bg-gray-100 rounded-lg animate-pulse w-full" />
                    ) : (
                        <select
                            value={selectedTournament?.CLAVE ?? ''}
                            onChange={e => {
                                const t = tournaments.find(x => x.CLAVE === Number(e.target.value)) ?? null;
                                setSelectedTournament(t);
                                setActiveTab('unpaid');
                            }}
                            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
                        >
                            <option value="">— Choose a tournament —</option>
                            {tournaments.map(t => (
                                <option key={t.CLAVE} value={t.CLAVE}>
                                    {t.NOMBRE}
                                    {t.FECHAINICIO ? ` (${new Date(t.FECHAINICIO).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })})` : ''}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Empty state */}
                {!selectedTournament && (
                    <div className="text-center py-20 text-gray-400">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <p className="text-lg font-medium">Select a tournament to view web fees</p>
                    </div>
                )}

                {/* Loading entries */}
                {selectedTournament && entriesLoading && (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
                    </div>
                )}

                {/* Error */}
                {selectedTournament && entriesError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{entriesError}</div>
                )}

                {/* Tabs + content */}
                {selectedTournament && !entriesLoading && !entriesError && (
                    <>
                        {/* Tab bar */}
                        <div className="flex gap-3 mb-6">
                            {tabs.map(t => {
                                const isActive = activeTab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setActiveTab(t.id)}
                                        className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${isActive ? t.active : t.inactive}`}
                                    >
                                        {t.label}
                                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isActive ? t.badgeActive : t.badge}`}>
                                            {t.count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* ── Not Paid ─────────────────────────────────────────────────────────────── */}
                        {activeTab === 'unpaid' && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-gray-600 text-sm">
                                        {unpaid.length} bowler{unpaid.length !== 1 ? 's' : ''} have not paid the web fee
                                    </p>
                                    <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-2 text-rose-800 font-semibold">
                                        Total Owed: ${totalOwed.toLocaleString()}
                                    </div>
                                </div>
                                <div className="bg-white rounded-2xl shadow overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-rose-50 border-b border-rose-100">
                                            <tr>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-rose-700 uppercase tracking-wider">TAT #</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-rose-700 uppercase tracking-wider">Bowler</th>
                                                <th className="text-right px-6 py-3 text-xs font-semibold text-rose-700 uppercase tracking-wider">Amount Due</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {unpaid.length === 0 ? (
                                                <tr><td colSpan={3} className="text-center py-12 text-gray-400">âœ“ All bowlers have paid</td></tr>
                                            ) : unpaid.map((e, i) => (
                                                <tr key={entryKey(e)} className={i % 2 === 0 ? 'bg-white' : 'bg-rose-50/20'}>
                                                    <td className="px-6 py-3 text-sm font-mono text-gray-700">{e.FICHANAL}</td>
                                                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{e.NOMBRECOMPLETO}</td>
                                                    <td className="px-6 py-3 text-sm text-right">
                                                        <span className="inline-block bg-rose-50 text-rose-700 font-semibold px-2.5 py-0.5 rounded-full border border-rose-200">
                                                            ${WEBFEE_AMOUNT}.00
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ── Paid ─────────────────────────────────────────────────────────────── */}
                        {activeTab === 'paid' && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-gray-600 text-sm">
                                        {paid.length} bowler{paid.length !== 1 ? 's' : ''} have paid the web fee
                                    </p>
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-emerald-800 font-semibold">
                                        Total Collected: ${totalPaid.toLocaleString()}
                                    </div>
                                </div>
                                <div className="bg-white rounded-2xl shadow overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-emerald-50 border-b border-emerald-100">
                                            <tr>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-700 uppercase tracking-wider">TAT #</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-700 uppercase tracking-wider">Bowler</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-emerald-700 uppercase tracking-wider">Source</th>
                                                <th className="text-right px-6 py-3 text-xs font-semibold text-emerald-700 uppercase tracking-wider">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {paid.length === 0 ? (
                                                <tr><td colSpan={4} className="text-center py-12 text-gray-400">No paid entries yet</td></tr>
                                            ) : paid.map((e, i) => (
                                                <tr key={entryKey(e)} className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/20'}>
                                                    <td className="px-6 py-3 text-sm font-mono text-gray-700">{e.FICHANAL}</td>
                                                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{e.NOMBRECOMPLETO}</td>
                                                    <td className="px-6 py-3 text-sm">
                                                        {e.CVEVENDEDOR_WEBFEE != null
                                                            ? <span className="inline-block bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-indigo-200">POS #{e.CVEVENDEDOR_WEBFEE}</span>
                                                            : <span className="inline-block bg-sky-50 text-sky-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-sky-200">Online</span>}
                                                    </td>
                                                    <td className="px-6 py-3 text-sm text-right">
                                                        <span className="inline-block bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-0.5 rounded-full border border-emerald-200">
                                                            ${WEBFEE_AMOUNT}.00
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeTab === 'pos' && (
                            <div>
                                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                    <p className="text-gray-600 text-sm">
                                        {pos.length} bowler{pos.length !== 1 ? 's' : ''} paid at the POS
                                        {excludedKeys.size > 0 && (
                                            <span className="ml-2 text-amber-600 font-medium">
                                                ({excludedKeys.size} excluded from invoice)
                                            </span>
                                        )}
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2 text-indigo-800 font-semibold">
                                            Invoice Total: ${posInvoiceTotal.toLocaleString()}
                                        </div>
                                        <button
                                            onClick={handlePrintInvoice}
                                            disabled={posForInvoice.length === 0}
                                            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                            </svg>
                                            Print Invoice
                                        </button>
                                    </div>
                                </div>
                                <div className="bg-white rounded-2xl shadow overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-indigo-50 border-b border-indigo-100">
                                            <tr>
                                                <th className="px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider text-left w-12">Excl.</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">TAT #</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">Bowler</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">POS Vendor</th>
                                                <th className="text-left px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">Date Paid</th>
                                                <th className="text-right px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {pos.length === 0 ? (
                                                <tr><td colSpan={6} className="text-center py-12 text-gray-400">No POS payments found</td></tr>
                                            ) : pos.map((e, i) => {
                                                const key = entryKey(e);
                                                const excluded = excludedKeys.has(key);
                                                return (
                                                    <tr
                                                        key={key}
                                                        className={`transition ${excluded ? 'opacity-40 bg-gray-50' : i % 2 === 0 ? 'bg-white' : 'bg-indigo-50/20'}`}
                                                    >
                                                        <td className="px-6 py-3 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={excluded}
                                                                onChange={() => toggleExclude(e)}
                                                                title="Exclude from invoice"
                                                                className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className={`px-6 py-3 text-sm font-mono text-gray-700 ${excluded ? 'line-through' : ''}`}>{e.FICHANAL}</td>
                                                        <td className={`px-6 py-3 text-sm font-medium text-gray-900 ${excluded ? 'line-through' : ''}`}>{e.NOMBRECOMPLETO}</td>
                                                        <td className="px-6 py-3 text-sm">
                                                            <span className="inline-block bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-indigo-200">
                                                                #{e.CVEVENDEDOR_WEBFEE}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-gray-600">{formatDate(e.FECHA_PAGO_WEBFEE)}</td>
                                                        <td className="px-6 py-3 text-sm text-right">
                                                            <span className={`inline-block font-semibold px-2.5 py-0.5 rounded-full border ${excluded ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                                                                ${WEBFEE_AMOUNT}.00
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        {pos.length > 0 && (
                                            <tfoot className="bg-indigo-50 border-t-2 border-indigo-200">
                                                <tr>
                                                    <td colSpan={5} className="px-6 py-3 text-sm font-bold text-indigo-800">
                                                        Invoice Total ({posForInvoice.length} of {pos.length} entries)
                                                    </td>
                                                    <td className="px-6 py-3 text-right font-bold text-indigo-900 text-base">
                                                        ${posInvoiceTotal.toLocaleString()}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="h-12" />
            </div>
        </div>
    );
};

export default WebFees;
